// src/js/components/sliders.js
if (!customElements.get("slider-component") || !customElements.get("slideshow-component")) {
  class SliderComponent extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('[id^="Slider-"]');
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.enableSliderLooping = false;
      this.currentPageElement = this.querySelector(".slider-counter--current");
      this.pageTotalElement = this.querySelector(".slider-counter--total");
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
      if (!this.slider) return;
      this.initPages();
      const resizeObserver = new ResizeObserver((entries) => this.initPages());
      resizeObserver.observe(this.slider);
      let observerOptions = {
        root: this,
        threshold: 0.5
      };
      var observer = new IntersectionObserver(this.intersectionCallback.bind(this), observerOptions);
      this.sliderItems.forEach((sliderItem) => {
        observer.observe(sliderItem);
      });
      this.prevButton?.addEventListener("click", this.onButtonClick.bind(this));
      this.nextButton?.addEventListener("click", this.onButtonClick.bind(this));
    }
    initPages() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
      if (this.sliderItemsToShow.length < 2) return;
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
      this.slidesPerPage = Math.floor(
        (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
      );
      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
      this.update();
    }
    resetPages() {
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.initPages();
    }
    intersectionCallback(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.update();
        }
      });
    }
    update() {
      if (!this.slider || !this.nextButton) return;
      const previousPage = this.currentPage;
      this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;
      if (this.currentPageElement && this.pageTotalElement) {
        this.currentPageElement.textContent = this.currentPage;
        this.pageTotalElement.textContent = this.totalPages;
      }
      if (this.currentPage != previousPage) {
        this.dispatchEvent(
          new CustomEvent("slideChanged", {
            detail: {
              currentPage: this.currentPage,
              currentElement: this.sliderItemsToShow[this.currentPage - 1]
            }
          })
        );
      }
      if (this.enableSliderLooping) return;
      if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
        this.prevButton.setAttribute("disabled", "disabled");
      } else {
        this.prevButton.removeAttribute("disabled");
      }
      if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
        this.nextButton.setAttribute("disabled", "disabled");
      } else {
        this.nextButton.removeAttribute("disabled");
      }
    }
    isSlideVisible(element, offset = 0) {
      const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
      return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
    }
    onButtonClick(event) {
      event.preventDefault();
      const step = event.currentTarget.name === "next" ? 1 : -1;
      this.setSlidePosition(step);
    }
    setSlidePosition(step) {
      this.slideScrollPosition = this.slider.scrollLeft + step * this.sliderItemOffset;
      this.slider.scrollTo({
        left: this.slideScrollPosition
      });
    }
  }
  customElements.define("slider-component", SliderComponent);
  class SlideshowComponent extends SliderComponent {
    constructor() {
      super();
      this.sliderControlWrapper = this.querySelector(".slider-buttons");
      this.enableSliderLooping = true;
      if (!this.sliderControlWrapper) return;
      this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
      if (this.sliderItemsToShow.length > 0) this.currentPage = 1;
      this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
      this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;
      this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll(".slider-counter__link"));
      this.sliderControlLinksArray.forEach((link) => link.addEventListener("click", this.linkToSlide.bind(this)));
      this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
      this.setSlideVisibility();
      if (this.announcementBarSlider) {
        this.announcementBarArrowButtonWasClicked = false;
        this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
        this.reducedMotion.addEventListener("change", () => {
          if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
        });
        [this.prevButton, this.nextButton].forEach((button) => {
          button.addEventListener(
            "click",
            () => {
              this.announcementBarArrowButtonWasClicked = true;
            },
            { once: true }
          );
        });
      }
      if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
    }
    setAutoPlay() {
      this.autoplaySpeed = this.slider.dataset.speed * 1e3;
      this.addEventListener("mouseover", this.focusInHandling.bind(this));
      this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
      this.addEventListener("focusin", this.focusInHandling.bind(this));
      this.addEventListener("focusout", this.focusOutHandling.bind(this));
      if (this.querySelector(".slideshow__autoplay")) {
        this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
        this.sliderAutoplayButton.addEventListener("click", this.autoPlayToggle.bind(this));
        this.autoplayButtonIsSetToPlay = true;
        this.play();
      } else {
        this.reducedMotion?.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
      }
    }
    onButtonClick(event) {
      this.wasClicked = true;
      const isFirstSlide = this.currentPage === 1;
      const isLastSlide = this.currentPage === this.sliderItemsToShow.length;
      if (isFirstSlide && event.currentTarget.name === "previous") {
        this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
      } else if (isLastSlide && event.currentTarget.name === "next") {
        this.slideScrollPosition = 0;
      } else {
        const step = event.currentTarget.name === "next" ? 1 : -1;
        this.slideScrollPosition = this.slider.scrollLeft + step * this.sliderItemOffset;
      }
      this.setSlidePosition(this.slideScrollPosition);
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
    }
    setSlidePosition(position) {
      if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
      this.setPositionTimeout = setTimeout(() => {
        this.slider.scrollTo({
          left: position
        });
      }, this.announcerBarAnimationDelay);
    }
    update() {
      super.update();
      this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
      this.prevButton.removeAttribute("disabled");
      if (!this.sliderControlButtons.length) return;
      this.sliderControlButtons.forEach((link) => {
        link.classList.remove("slider-counter__link--active");
        link.removeAttribute("aria-current");
      });
      this.sliderControlButtons[this.currentPage - 1].classList.add("slider-counter__link--active");
      this.sliderControlButtons[this.currentPage - 1].setAttribute("aria-current", true);
    }
    autoPlayToggle() {
      this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
      this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
      this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
    }
    focusOutHandling(event) {
      if (this.sliderAutoplayButton) {
        const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
        if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
        this.play();
      } else if (!this.reducedMotion?.matches && !this.announcementBarArrowButtonWasClicked) {
        this.play();
      }
    }
    focusInHandling(event) {
      if (this.sliderAutoplayButton) {
        const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
        if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
          this.play();
        } else if (this.autoplayButtonIsSetToPlay) {
          this.pause();
        }
      } else if (this.announcementBarSlider?.contains(event.target)) {
        this.pause();
      }
    }
    play() {
      this.slider.setAttribute("aria-live", "off");
      clearInterval(this.autoplay);
      this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
    }
    pause() {
      this.slider.setAttribute("aria-live", "polite");
      clearInterval(this.autoplay);
    }
    togglePlayButtonState(pauseAutoplay) {
      if (pauseAutoplay) {
        this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
        this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.playSlideshow);
      } else {
        this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
        this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.pauseSlideshow);
      }
    }
    autoRotateSlides() {
      const slideScrollPosition = this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;
      this.setSlidePosition(slideScrollPosition);
      this.applyAnimationToAnnouncementBar();
    }
    setSlideVisibility(event) {
      this.sliderItemsToShow.forEach((item, index) => {
        const linkElements = item.querySelectorAll("a");
        if (index === this.currentPage - 1) {
          if (linkElements.length)
            linkElements.forEach((button) => {
              button.removeAttribute("tabindex");
            });
          item.setAttribute("aria-hidden", "false");
          item.removeAttribute("tabindex");
        } else {
          if (linkElements.length)
            linkElements.forEach((button) => {
              button.setAttribute("tabindex", "-1");
            });
          item.setAttribute("aria-hidden", "true");
          item.setAttribute("tabindex", "-1");
        }
      });
      this.wasClicked = false;
    }
    applyAnimationToAnnouncementBar(button = "next") {
      if (!this.announcementBarSlider) return;
      const itemsCount = this.sliderItems.length;
      const increment = button === "next" ? 1 : -1;
      const currentIndex = this.currentPage - 1;
      let nextIndex = (currentIndex + increment) % itemsCount;
      nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;
      const nextSlide = this.sliderItems[nextIndex];
      const currentSlide = this.sliderItems[currentIndex];
      const animationClassIn = "announcement-bar-slider--fade-in";
      const animationClassOut = "announcement-bar-slider--fade-out";
      const isFirstSlide = currentIndex === 0;
      const isLastSlide = currentIndex === itemsCount - 1;
      const shouldMoveNext = button === "next" && !isLastSlide || button === "previous" && isFirstSlide;
      const direction = shouldMoveNext ? "next" : "previous";
      currentSlide.classList.add(`${animationClassOut}-${direction}`);
      nextSlide.classList.add(`${animationClassIn}-${direction}`);
      setTimeout(() => {
        currentSlide.classList.remove(`${animationClassOut}-${direction}`);
        nextSlide.classList.remove(`${animationClassIn}-${direction}`);
      }, this.announcerBarAnimationDelay * 2);
    }
    linkToSlide(event) {
      event.preventDefault();
      const slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
      this.slider.scrollTo({
        left: slideScrollPosition
      });
    }
  }
  customElements.define("slideshow-component", SlideshowComponent);
}
